<template>
  <div class="media-gallery">
    <Lightbox
      :media="media"
      :isVisible="isLightboxVisible"
      :currentMediumIndex="currentMediumIndex"
      :showNavigationButtons="showLightboxNavigationButtons"
      class="media-gallery__lightbox"
      @visibility-change="onLightboxVisibilityChange"
      @medium-change="onMediumIndexChangeLightbox"
    >
      <template slot="medium" slot-scope="{ currentMedium }">
        <iframe
          v-if="isTypeVideo(currentMedium.type)"
          ref="lightboxVideo"
          :src="currentMedium.url"
          frameborder="0"
          allow="autoplay; fullscreen"
          webkitallowfullscreen
          mozallowfullscreen
          allowfullscreen
          class="media-gallery__lightbox-video"
        ></iframe>
        <ImageContentful
          v-else
          :src="currentMedium.url"
          containerSelector=".media-gallery__lightbox"
          :options="{ imageCompression: 71, setHeight: false }"
          :lazyload="{ rootMargin: '0px' }"
          class="media-gallery__lightbox-image"
        />
      </template>
      <template slot="caption" slot-scope="{ currentMedium }">
        <MediaCaption v-if="isTypeImage(currentMedium.type)" :medium="currentMedium"></MediaCaption>
      </template>
    </Lightbox>
    <Carousel
      ref="carousel"
      :perPage="1"
      :spacePadding="carouselSpaceRight"
      :spacePaddingMaxOffsetFactor="0"
      :navigate-to="currentMediumIndex"
      :speed="transitionSpeed"
      :minSwipeDistance="6"
      :scrollPerPage="false"
      :loop="false"
      :paginationEnabled="showCarouselPagination"
      :navigationEnabled="true"
      navigationNextLabel
      navigationPrevLabel
      class="media-gallery__carousel"
      @pageChange="onCarouselImageChange"
    >
      <Slide
        v-for="(medium, index) in media"
        :key="index"
        :style="carouselSlideStyle"
        :class="{ 'media-gallery__carousel-slide--interactive': isSlideClickable(medium) }"
        class="media-gallery__carousel-slide"
        @slideclick="onSlideClick(medium, index)"
      >
        <div class="media-gallery__carousel-slide-inner">
          <ImageContentful
            :isBackgroundImage="useBackgroundImages"
            :src="medium.previewUrl || medium.url"
            containerSelector=".VueCarousel-inner"
            :options="{
              imageCompression: 71,
              ...(useBackgroundImages && { fit: CONTENTFUL_IMAGE_FIT.fill }),
              ...(!useBackgroundImages && { setHeight: false }),
            }"
            :lazyload="{ rootMargin: '200px' }"
            class="media-gallery__carousel-image"
            :class="{ 'media-gallery__carousel-image--background': useBackgroundImages }"
          />
          <MediaCaption
            :medium="medium"
            :variant="captionVariant"
            class="media-gallery__carousel-caption"
            @playclick="onPlayClick"
          ></MediaCaption>
        </div>
      </Slide>
    </Carousel>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { Carousel, Slide } from 'vue-carousel/src'
import VimeoPlayer from '@vimeo/player'
import ImageContentful, { CONTENTFUL_IMAGE_FIT } from '@/components/_shared/ImageContentful.vue'
import Lightbox from '@/components/_shared/Lightbox/Lightbox.vue'
import MediaCaption, { VARIANTS } from './MediaCaption.vue'
import { MEDIA_TYPES, isTypeImage, isTypeVideo } from './media-types'

export const CAPTION_VARIANTS = VARIANTS

const TRANSITION_SPEED_DEFAULT = 400
const CAROUSEL_SPACE_RIGHT_MOBILE = 20
const CAROUSEL_SPACE_RIGHT_DESKTOP = 100
const CAROUSEL_SLIDE_PADDING_RIGHT_MOBILE = 6
const CAROUSEL_SLIDE_PADDING_RIGHT_DESKTOP = 30
const CURRENT_MEDIUM_INDEX_DEFAULT = 0

export default {
  name: 'MediaGallery',
  components: {
    Carousel,
    Slide,
    ImageContentful,
    Lightbox,
    MediaCaption,
  },

  props: {
    /**
     * List of medium to be shown in the media gallery.
     * IMPORTANT: For type 'video' currently autoplay is only supported for vimeo (like: https://player.vimeo.com/video/<id>)
     *
     *  @example
     * [
     *  {
     *    type: 'image',
     *    url: 'http://.../some-image.jpg',
     *    title: 'Title',
     *    description: 'Text...',
     *  },
     *  {
     *    type: 'video',
     *    url: 'https://player.vimeo.com/video/<id>', // only vimeo supported for now
     *    previewUrl: 'http://.../some-image.jpg', // for videos a preview image is shown in the carousel
     *    title: 'Title',
     *    description: 'Text...',
     *  },
     */
    media: {
      type: Array,
      required: true,
      validator(media) {
        return media.every(medium => {
          return (
            ['type', 'url'].every(key => medium[key]) &&
            (medium.type !== MEDIA_TYPES.video || !!medium.previewUrl)
          )
        })
      },
    },
    captionVariant: {
      type: String,
      validator(value) {
        return Object.values(CAPTION_VARIANTS).includes(value)
      },
    },
    /**
     * Determines how much right padding there is between each slide on mobile.
     */
    carouselSlidePaddingRightMobile: {
      type: Number,
      default: CAROUSEL_SLIDE_PADDING_RIGHT_MOBILE,
    },
    /**
     * Determines how much right padding there is between each slide on desktop.
     */
    carouselSlidePaddingRightDesktop: {
      type: Number,
      default: CAROUSEL_SLIDE_PADDING_RIGHT_DESKTOP,
    },
    /**
     * Determines how much spacing there is to the right on mobile (to see the next slide).
     */
    carouselSpaceRightMobile: {
      type: Number,
      default: CAROUSEL_SPACE_RIGHT_MOBILE,
    },
    /**
     * Determines how much spacing there is to the right on desktop (to see the next slide).
     */
    carouselSpaceRightDesktop: {
      type: Number,
      default: CAROUSEL_SPACE_RIGHT_DESKTOP,
    },
    /**
     * Determines if background images are used for each carousel image.
     */
    useBackgroundImages: {
      type: Boolean,
      default: false,
    },
    /**
     * Shows/hides buttons to navigate between media in the lightbox
     */
    showCarouselPagination: {
      type: Boolean,
      default: false,
    },
    /**
     * Shows/hides buttons to navigate between media in the lightbox
     */
    showLightboxNavigationButtons: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      currentMediumIndex: CURRENT_MEDIUM_INDEX_DEFAULT,
      currentMediumIndexLightbox: CURRENT_MEDIUM_INDEX_DEFAULT,
      transitionSpeed: TRANSITION_SPEED_DEFAULT,
      isLightboxVisible: false,
      vimeoPlayer: null,
      CONTENTFUL_IMAGE_FIT,
    }
  },

  computed: {
    ...mapGetters({
      isMqLarge: 'viewport/isMqLarge',
      isMqCinema: 'viewport/isMqCinema',
      shouldAvoidBlockToggling: 'nav/shouldAvoidBlockToggling',
    }),
    carouselImagesPerPage() {
      return this.isMqCinema ? 2 : 1
    },
    carouselSlidePaddingRight() {
      return this.isMqLarge
        ? this.carouselSlidePaddingRightDesktop
        : this.carouselSlidePaddingRightMobile
    },
    carouselSpaceRight() {
      return this.isMqLarge ? this.carouselSpaceRightDesktop : this.carouselSpaceRightMobile
    },
    carouselSlideStyle() {
      return {
        'padding-right': this.carouselSlidePaddingRight,
      }
    },
    currentMediumLightbox() {
      return this.media[this.currentMediumIndexLightbox]
    },
  },

  watch: {
    media: 'onMediaChange',
  },

  mounted() {
    this.updatePlayer()
  },

  methods: {
    isTypeImage(type) {
      return isTypeImage(type)
    },

    isTypeVideo(type) {
      return isTypeVideo(type)
    },

    isSlideClickable(medium) {
      return this.isMqLarge || this.isTypeImage(medium.type)
    },

    onSlideClick(medium, index) {
      if (this.isSlideClickable(medium)) {
        this.openLightbox(index)
      }
    },

    onPlayClick(medium) {
      const index = this.getMediumIndex(medium)
      this.openLightbox(index)
    },

    openLightbox(index) {
      this.currentMediumIndex = index
      this.isLightboxVisible = true
    },

    onLightboxVisibilityChange(isVisible, e) {
      if (!this.isLightboxVisible) {
        this.transitionSpeed = 0
        this.playVideo()
      } else {
        // set dropdownNavIsBlocked to prevent showing mini-cart via setAdditionalNav
        // WARNING: this is based on the assumption that lightbox close button and mini-cart button share the same position in 2D
        this.conditinallyBlockDropdownNav(e)
        this.currentMediumIndex = this.currentMediumIndexLightbox
        setTimeout(() => {
          this.transitionSpeed = TRANSITION_SPEED_DEFAULT
          this.isLightboxVisible = isVisible
        }, TRANSITION_SPEED_DEFAULT)
        this.pauseVideo()
      }
    },

    onMediumIndexChangeLightbox(index) {
      this.currentMediumIndexLightbox = index
      this.updatePlayer()
    },

    onCarouselImageChange(index) {
      this.currentMediumIndex = index
    },

    onMediaChange() {
      this.currentMediumIndex = CURRENT_MEDIUM_INDEX_DEFAULT // always start with first medium
    },

    conditinallyBlockDropdownNav(e) {
      if (e && !this.shouldAvoidBlockToggling(e)) {
        this.$store.commit('nav/blockDropdownNav')
      }
    },

    async updatePlayer() {
      await this.$nextTick()
      if (this.$refs.lightboxVideo) {
        try {
          this.vimeoPlayer = new VimeoPlayer(this.$refs.lightboxVideo)
          if (this.isLightboxVisible) this.playVideo()
        } catch (e) {
          const errorMessage = `Autoplay in MediaGallery is not working for ${JSON.stringify(
            this.currentMediumLightbox
          )}: ${e.message}`
          this.$rollbar.error(errorMessage, {
            currentMediumLightbox: this.currentMediumLightbox,
            error: e,
          })
        }
      }
    },

    playVideo() {
      if (this.vimeoPlayer) return this.vimeoPlayer.play()
    },

    pauseVideo() {
      if (this.vimeoPlayer) return this.vimeoPlayer.pause()
    },

    getMediumIndex(medium) {
      return this.media.indexOf(medium)
    },
  },
}
</script>

<style lang="scss" scoped>
$next-button-width: 40px;
$next-button-margin: 20px;
$pagination-dot-size: 7px;

/deep/ .media-gallery {
  &__carousel-slide {
    &--interactive {
      .media-gallery__carousel-slide-inner {
        cursor: pointer;
      }
    }
  }
  &__carousel-slide-inner {
    position: relative;
    height: 100%;
  }
}

.media-gallery {
  &__carousel-image {
    width: 100%;
    vertical-align: bottom;

    &--background {
      width: 100%;
      height: 100%;
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center;
    }
  }

  /deep/ .VueCarousel {
    &-slide {
      padding-right: 6px;

      @media ($mq-large) {
        padding-right: 30px;
      }
    }

    &-inner {
      padding-left: 0 !important; // only have spacePadding on the right to always show part of next media
    }

    &-navigation-button {
      display: none;
      width: $next-button-width;
      height: $next-button-width;
      margin: 0 $next-button-margin !important;
      padding: 0 !important;
      transform: translateY(-50%);
      color: $color-black;
      background-color: $color-white;
      border-radius: 100%;
      opacity: 0.4;
      outline: 0;

      &:focus {
        outline: 0;
      }

      @media ($mq-large) {
        display: block;
      }
    }

    &-navigation-button:hover {
      opacity: 0.6;
    }

    &-navigation-prev:before,
    &-navigation-next:after {
      @include icomoon-base-props;
      content: '\e926';
      display: inline-block;
      line-height: $next-button-width;
    }

    &-navigation-prev:before {
      transform: rotate(180deg);
    }

    &-navigation--disabled {
      display: none;
    }

    &-pagination {
      position: absolute;
      bottom: 32px;
    }

    &-dot {
      width: $pagination-dot-size !important; // overwrite styles
      height: $pagination-dot-size !important; // overwrite styles
      margin: 0 !important; // overwrite styles
      padding: 4px !important; // overwrite styles
      background-color: $color-white !important; // overwrite styles
      opacity: 0.3;

      &--active {
        opacity: 1;
      }
    }
  }
}
</style>
